const AI_API_BASE_URL = process.env.REACT_APP_AI_API_URL || '/api/v1/ai';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${window.sessionStorage.getItem('token') || localStorage.getItem('token')}`,
});

const handleResponse = async (response) => {
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `HTTP ${response.status}`);
  }
  return response.json();
};

export const getSessions = async (page = 1, pageSize = 50) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/sessions/?page=${page}&page_size=${pageSize}`,
    { headers: getHeaders() },
  );
  return handleResponse(res);
};

export const createSession = async ({ title, roleId }) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ title, role_id: roleId }),
  });
  return handleResponse(res);
};

export const getSession = async (sessionId) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
};

export const updateSession = async (sessionId, { title, status }) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ title, status }),
  });
  return handleResponse(res);
};

export const deleteSession = async (sessionId) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (res.status === 204) return;
  return handleResponse(res);
};

export const archiveSession = async (sessionId) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}/archive`, {
    method: 'PATCH',
    headers: getHeaders(),
  });
  return handleResponse(res);
};

export const getSessionStats = async (sessionId) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}/stats`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
};

export const getSessionMessages = async (sessionId, page = 1, pageSize = 50) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/sessions/${sessionId}/messages/?page=${page}&page_size=${pageSize}`,
    { headers: getHeaders() },
  );
  return handleResponse(res);
};

export const createMessage = async (sessionId, { content, role, modelId }) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}/messages/`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      content,
      role,
      session_id: sessionId,
      model_id: modelId,
    }),
  });
  return handleResponse(res);
};

export const generateSessionTitle = async (sessionId) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}/title`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return handleResponse(res);
};

export const processSessionMessage = async (sessionId, { content, modelId, kbId }) => {
  const res = await fetch(`${AI_API_BASE_URL}/sessions/${sessionId}/process/`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      content,
      model_id: modelId,
      kb_id: kbId,
    }),
  });
  return handleResponse(res);
};

export const getRoles = async (page = 1, pageSize = 50) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/roles/?page=${page}&page_size=${pageSize}`,
    { headers: getHeaders() },
  );
  return handleResponse(res);
};

export const getKnowledgeBases = async (page = 1, pageSize = 100) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/knowledge-bases/?page=${page}&page_size=${pageSize}`,
    { headers: getHeaders() },
  );
  return handleResponse(res);
};

export const getModels = async (page = 1, pageSize = 50) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/models/?page=${page}&page_size=${pageSize}`,
    { headers: getHeaders() },
  );
  return handleResponse(res);
};

export const getPendingMCPCalls = async (sessionId, messageId) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/sessions/${sessionId}/process/${messageId}/mcp/pending`,
    { headers: getHeaders() },
  );
  return handleResponse(res);
};

export const approveMCPCall = async (sessionId, messageId, callId) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/sessions/${sessionId}/process/${messageId}/mcp/approve/${callId}`,
    { method: 'POST', headers: getHeaders() },
  );
  return handleResponse(res);
};

export const rejectMCPCall = async (sessionId, messageId, callId) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/sessions/${sessionId}/process/${messageId}/mcp/reject/${callId}`,
    { method: 'POST', headers: getHeaders() },
  );
  return handleResponse(res);
};

export const getMcpTools = async () => {
  const res = await fetch(`${AI_API_BASE_URL}/mcp-tools`, {
    headers: getHeaders(),
  });
  return handleResponse(res);
};

export const togglePinMessage = async (sessionId, messageId, isPinned) => {
  const res = await fetch(
    `${AI_API_BASE_URL}/sessions/${sessionId}/messages/${messageId}/pin`,
    {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ is_pinned: isPinned }),
    },
  );
  return handleResponse(res);
};

export default {
  getSessions,
  createSession,
  getSession,
  updateSession,
  deleteSession,
  archiveSession,
  getSessionStats,
  getSessionMessages,
  createMessage,
  processSessionMessage,
  generateSessionTitle,
  getRoles,
  getModels,
  getKnowledgeBases,
  getPendingMCPCalls,
  approveMCPCall,
  rejectMCPCall,
  getMcpTools,
  togglePinMessage,
};
